import './styles.scss';

import { Badge, Button } from 'react-bootstrap';
import { Datetime, createNotification, getClasses } from '@/utils';
import { ManifestWithRoutes, Route, RouteStatusEnum, convertDtrLocationToPotentialLocation } from '../../api/services';

import Input from '@/components/Input';
import Tippy from '@tippyjs/react';
import { Toast } from '@/models';
import { deleteManifestHistoryRouteBulk } from '@/features/DriveTimeTool/api/services/deleteManifestHistoryRouteBulk';
import { deleteQueryHistoryBulk } from '@/features/DriveTimeTool/api/services/deleteQueryHistoryBulk';
import { exportPredictiveDriveTimeReportToCsv } from '../../api/services/getPredictiveDriveTimeReportAsCsv';
import { formatFullAddress } from '../../utils/locations';
import { generateAndDownloadDriveTimePDF } from '../../api/services/generateDriveTimeReportPDF';
import useConfirmation from '@/hooks/useConfirmation';
import { useDriveTimeTool } from '@/features/DriveTimeTool';
import { useMemo } from 'react';
import { useRouteMap } from '../RouteMap';
import { useState } from 'react';

interface RouteCardProps {
  route: Route;
  condensed?: boolean;
}

const RouteCard = ({ route, condensed }: RouteCardProps): JSX.Element => {
  const [gettingCsv, setGettingCsv] = useState<boolean>(false);
  const [generatingPdf, setGeneratingPdf] = useState<boolean>(false);
  const [state, setState] = useDriveTimeTool(({ state, setState }) => [
    {
      history: state.history.flatMap((item) => item.routes),
      puLocation: state.locations.get(route.puLocationId),
      doLocation: state.locations.get(route.doLocationId),
      isSelected: state.selected === route.id,
      createdAt: route.createdAt,
    },
    setState,
  ]);

  const confirmDelete = useConfirmation({
    Body: {
      title: 'Delete Query History',
      message: 'Are you sure you want to delete this query history?',
    },
  });
  const handleDelete = async () => {
    try {
      await confirmDelete();
      try {
        const fn = route.manifestHistoryId ? deleteManifestHistoryRouteBulk : deleteQueryHistoryBulk;
        const id = route.manifestHistoryId ? route.routeId : route.id;
        const res = await fn([id]);

        setState((current) => {
          const history: ManifestWithRoutes[] = [];
          for (const temp of current.history) {
            temp.routes = temp.routes.filter((item) => item.id !== route.id);
            if (!temp.routes.length) continue;
            history.push(temp);
          }
          return {
            ...current,
            selected: current.selected === route.id ? undefined : current.selected,
            selectedFrom: current.selected === route.id ? undefined : current.selectedFrom,
            history,
          };
        });
        console.log('Delete successful:', res);
      } catch (err) {
        console.error('Failed to delete query history:', err);
        createNotification('Failed to delete query history. Please try again.', Toast.Type.DANGER, 'Delete Failed');
      }
    } catch (err) {
      console.log('Delete rejected.');
    }
  };

  const handleSelect = () => {
    setState((current) => {
      return {
        ...current,
        selected: state.isSelected ? null : route.id,
        selectedFrom: state.isSelected ? null : route.manifestHistoryId ? 'manifestHistoryRouteId' : 'queryHistoryId',
      };
    });
    useRouteMap.getState().setState((current) => ({
      ...current,
      puAddress: !state.isSelected ? formatFullAddress(convertDtrLocationToPotentialLocation(state.puLocation)) : '',
      doAddress: !state.isSelected ? formatFullAddress(convertDtrLocationToPotentialLocation(state.doLocation)) : '',
    }));
  };

  const exportToCsv = (id: string) => async (): Promise<void> => {
    setGettingCsv(true);
    await exportPredictiveDriveTimeReportToCsv(
      id,
      route.manifestHistoryId ? 'manifestHistoryRouteId' : 'queryHistoryId',
      useDriveTimeTool.getState().state.timeFrameId?.split('||')[0]
    );
    setGettingCsv(false);
  };

  const generatePDF = (queryHistoryId: string, manifestHistoryId?: string) => async (): Promise<void> => {
    try {
      setGeneratingPdf(true);
      await generateAndDownloadDriveTimePDF(queryHistoryId, manifestHistoryId);
    } catch (err) {
      // create a notification if it failed
      createNotification(err.message || err, Toast.Type.DANGER, 'PDF Generation Failed');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const Status = useMemo(
    () => (
      <Badge bg={getStatusVariant(route.status)} text={getStatusTextVariant(route.status)} pill className="fw-semibold">
        {getStatusName(route.status)}
      </Badge>
    ),
    [route.status]
  );
  const Controls = useMemo(
    () => (
      <div className="RouteControls">
        <Button variant="icon" onClick={exportToCsv(route.id)} disabled={gettingCsv || route.status !== RouteStatusEnum.Completed}>
          <Tippy content="Export to CSV">
            <i className="sv sv-download2" />
          </Tippy>
        </Button>
        <Button
          variant="icon"
          onClick={generatePDF(route.id, route.routeId)}
          disabled={generatingPdf || route.status !== RouteStatusEnum.Completed}
        >
          <Tippy content="Export to PDF">
            <i className={getClasses(generatingPdf ? 'fa fa-spinner fa-pulse' : 'sv sv-file-charts')} />
          </Tippy>
        </Button>
        <Button variant="icon" onClick={handleDelete}>
          <Tippy content="Delete Query">
            <i className="sv sv-trash2" />
          </Tippy>
        </Button>
      </div>
    ),
    [exportToCsv, route.id, route.status, route.manifestHistoryId, gettingCsv, generatePDF, generatingPdf, handleDelete]
  );

  if (condensed)
    return (
      <div className="RouteCard shadow-box condensed">
        <div className="RouteCard-Select pt-1">
          {route.status === RouteStatusEnum.Completed && <Input type="checkbox" checked={state.isSelected} onChange={handleSelect} />}
          {route.status === RouteStatusEnum.Running && <i className="fas fa-spinner fa-pulse fs-5 text-muted" />}
          {route.status === RouteStatusEnum.Failed && <i className="fas fa-exclamation-triangle fs-5 text-danger" />}
        </div>
        <div className="RouteCard-Display">
          <div className="RouteCard-Location">
            <span className="icon-container icon-bg-success rounded-circle d-flex justify-content-center align-items-center">
              <i className="sv sv-clock3 text-green" />
            </span>
            <div>
              <span>{state.puLocation?.name || 'Unknown'}</span>
              <span>
                <i className="fa fa-arrow-right mx-2" />
              </span>
              <span>{state.doLocation?.name || 'Unknown'}</span>
            </div>
          </div>
          <div className="RouteCard-Status">{Status}</div>
        </div>
        {Controls}
      </div>
    );

  return (
    <div className="RouteCard shadow-box">
      <div className="RouteCard-Select pt-1">
        {route.status === RouteStatusEnum.Completed && <Input type="checkbox" checked={state.isSelected} onChange={handleSelect} />}
        {route.status === RouteStatusEnum.Running && <i className="fas fa-spinner fa-pulse fs-5 text-muted" />}
        {route.status === RouteStatusEnum.Failed && <i className="fas fa-exclamation-triangle fs-5 text-danger" />}
      </div>
      <div className="RouteCard-Display">
        <div className="RouteCard-Status">
          {Status}
          <Badge bg="none" className="SingleRoute">
            Single Route
          </Badge>
          {Controls}
        </div>
        <div className="RouteCard-Location">
          <span className="text-muted">Route:</span>
          <span>{state.puLocation?.name || 'Unknown'}</span>
          <span>
            <i className="fa fa-arrow-right" />
          </span>
          <span>{state.doLocation?.name || 'Unknown'}</span>
        </div>

        <div className="RouteCard-Schedule">
          <span>
            <i className="sv sv-calendar-empty" />
          </span>
          <span>Report Period:</span>
          <span>
            {new Datetime(route.startDate).format('MM/DD/YY')} to {new Datetime(route.endDate).format('MM/DD/YY')}
          </span>
          <span className="ms-4">
            <i className="sv sv-clock" />
          </span>
          <span>Created:</span>
          <span>{new Datetime().setAsUTC(route.createdAt).toLocaleDatetime().format('MM/DD/YY, HH:mm:ss')}</span>
        </div>
      </div>
    </div>
  );
};

const getStatusVariant = (status: RouteStatusEnum) => {
  switch (status) {
    case RouteStatusEnum.Completed:
      return 'success-subtle';
    case RouteStatusEnum.Running:
      return 'warning-subtle';
    case RouteStatusEnum.Failed:
      return 'danger-subtle';
    case RouteStatusEnum.InQueue:
      return 'info-subtle';
    default:
      return 'secondary-subtle';
  }
};

const getStatusTextVariant = (status: RouteStatusEnum) => {
  switch (status) {
    case RouteStatusEnum.Completed:
      return 'green';
    case RouteStatusEnum.Running:
      return 'warning';
    case RouteStatusEnum.Failed:
      return 'danger';
    case RouteStatusEnum.InQueue:
      return 'info';
    default:
      return 'secondary';
  }
};

const getStatusName = (status: RouteStatusEnum) => {
  switch (status) {
    case RouteStatusEnum.Completed:
      return 'Complete';
    case RouteStatusEnum.Running:
      return 'Processing';
    case RouteStatusEnum.Failed:
      return 'Failed';
    case RouteStatusEnum.InQueue:
      return 'In Queue';
    default:
      return 'Unknown';
  }
};
export default RouteCard;
